import React from "react";
import { View, Linking } from "react-native";
import { Controller } from "react-hook-form";
import { AppText } from "@/components/ui/AppText";
import { StepShell } from "../components/StepShell";
import { AppTextInput } from "@/components/ui/TextInput";
import { AppTextArea } from "@/components/ui/AppTextArea";
import { FileUpload } from "@/components/ui/FileUpload";
import { usePreVettingStep5Form } from "../hooks/usePreVettingStep5Form";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export default function PreVettingStep5Screen() {
  const { t } = useI18n();
  const {
    form,
    onFieldBlur,
    handleSubmit,
    handleBack,
    isSubmitting,
    submitError,
    logoFile,
    logoUploadState,
    logoUploadError,
    handleLogoFile,
    handleLogoRemove,
  } = usePreVettingStep5Form();

  const { errors } = form.formState;

  return (
    <StepShell
      step={5}
      isLastStep
      onNext={handleSubmit}
      nextLoading={isSubmitting}
      onBack={handleBack}
      iconName="palette"
      title="preVetting.step5.title"
      subtitle="preVetting.step5.subtitle"
    >
      <View className="items-start">
        <View className="tablet:flex-row tablet:gap-4 w-full">
          <View className="flex-1">
            <Controller
              control={form.control}
              name="business_short_name_english"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  label="preVetting.step5.shortNameEnglish"
                  value={value}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  required
                  hint="preVetting.step5.shortNameHint"
                  error={errors.business_short_name_english?.message as TranslationKeys | undefined}
                />
              )}
            />
          </View>

          <View className="flex-1">
            <Controller
              control={form.control}
              name="business_short_name_arabic"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  label="preVetting.step5.shortNameArabicOptional"
                  value={value ?? ""}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  isRTL
                />
              )}
            />
          </View>
        </View>

        <Controller
          control={form.control}
          name="primary_industry"
          render={({ field: { value, onChange, onBlur } }) => (
            <AppTextInput
              label="preVetting.step5.primaryIndustry"
              value={value}
              onChangeText={onChange}
              onBlur={() => {
                onBlur();
                onFieldBlur();
              }}
              required
              error={errors.primary_industry?.message as TranslationKeys | undefined}
            />
          )}
        />

        <Controller
          control={form.control}
          name="business_description_english"
          render={({ field: { value, onChange, onBlur } }) => (
            <AppTextArea
              label="preVetting.step5.descriptionEnglish"
              value={value}
              onChangeText={onChange}
              onBlur={() => {
                onBlur();
                onFieldBlur();
              }}
              numberOfLines={4}
              required
              error={errors.business_description_english?.message as TranslationKeys | undefined}
            />
          )}
        />
        <Controller
          control={form.control}
          name="business_description_arabic"
          render={({ field: { value, onChange, onBlur } }) => (
            <AppTextArea
              label="preVetting.step5.descriptionArabicOptional"
              value={value ?? ""}
              onChangeText={onChange}
              onBlur={() => {
                onBlur();
                onFieldBlur();
              }}
              numberOfLines={4}
              isRTL
            />
          )}
        />

        <FileUpload
          label="preVetting.step5.businessLogo"
          onFile={handleLogoFile}
          uploadState={logoUploadState}
          currentFile={logoFile}
          onViewFile={logoFile ? () => Linking.openURL(logoFile.uri) : undefined}
          onRemoveFile={handleLogoRemove}
          required
          error={logoUploadError ?? (errors.business_logo?.message as TranslationKeys | undefined)}
        />

        {submitError && (
          <AppText className="text-error text-sm mt-2">{t(submitError as TranslationKeys)}</AppText>
        )}
      </View>
    </StepShell>
  );
}
